/**
 * Toolbox panels — widget-first (2.1).
 * Stránka: pages/modules/toolbox.tsx → ModuleWorkspace + catalog.
 * Data: callWidgetTool (node-aware). Chrome kreslí WidgetShell.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../lib/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { StatCard, StatsGrid } from '../../../components/ui/Stats';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { ResourceTab } from '../toolbox-resources-limiter';
import AcmLinkPage from '../acm-link';
import { ModulePanel } from '../../../widgets/ModulePanel';
import { TOOLBOX_SYSTEM } from '../../../widgets/module-panels';
import { Button } from '../../../components/ui/Button';
import { RefreshButton } from '../../../components/ui/RefreshButton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { StatusDot } from '../../../components/ui/StatusDot';
import { Badge } from '../../../components/ui/Badge';
import { Label } from '../../../components/ui/Label';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { Tab, TabList, Tabs } from '../../../components/ui/Tabs';
import { Alert, AlertDescription } from '../../../components/ui/Alert';
import { TaskScheduler } from '../../../components/ui/TaskScheduler';
import { Select } from '../../../components/ui/Select';
import type { WidgetRenderProps } from '../../../widgets/types';
import { callMcp, getToolboxWidgetScope, setToolboxWidgetScope } from './scope';
import {
  Activity,
  AlertTriangle,
  Archive,
  ArrowUpCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  Download,
  Eye,
  FileText,
  Globe,
  HardDrive,
  Loader2,
  MapPin,
  Network,
  Play,
  RefreshCw,
  Server,
  Shield,
  Trash2,
  XCircle,
} from 'lucide-react';
import { themedConfirm } from '../../../lib/themed-dialogs';
import { UnfinishedProjects } from './UnfinishedProjects';


// ─── Helpers ────────────────────────────────────────────────────────────────

function formatBytes(b: number): string {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
}

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return i18n.t('toolbox.justNow', 'právě');
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const SEV_COLOR: Record<string, string> = {
  high: 'text-red-400 bg-red-500/10 border-red-500/20',
  med: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  low: 'text-gray-400 bg-gray-500/10 border-gray-500/20',
};

type SecurityScope = 'core' | 'module' | 'hosted';

type SecurityScanSummary = {
  high?: number;
  med?: number;
  low?: number;
  critical?: number;
  targets?: string[];
  password_leaks?: { status: 'complete' | 'incomplete'; checked: number; not_checked: number; exposed: number };
};

type SecurityFinding = {
  file: string;
  line: number;
  message: string;
  severity: string;
  category: string;
  rule_id?: string;
  content: string;
  target?: string;
};

const SECURITY_SCOPE_META: Record<
  SecurityScope,
  { labelKey: string; label: string; emptyKey: string; empty: string; badge: 'blue' | 'purple' | 'green' }
> = {
  core: { labelKey: 'toolbox.scopeCore', label: 'Core', emptyKey: 'toolbox.scopeCoreEmpty', empty: 'Core bez nálezů', badge: 'blue' },
  module: { labelKey: 'toolbox.scopeModules', label: 'Moduly', emptyKey: 'toolbox.scopeModulesEmpty', empty: 'Moduly bez nálezů', badge: 'purple' },
  hosted: { labelKey: 'toolbox.scopeHosted', label: 'Hostované aplikace', emptyKey: 'toolbox.scopeHostedEmpty', empty: 'Hostované aplikace bez nálezů', badge: 'green' },
};

function scopeForTarget(target?: string): SecurityScope {
  if (!target || target === 'acm-core' || target.startsWith('/opt/acm')) return 'core';
  if (target.startsWith('apphost:') || target.startsWith('codable:')) return 'hosted';
  return 'module';
}

function targetFromFinding(f: Pick<SecurityFinding, 'file' | 'target'>): string {
  if (f.target) return f.target;
  // Nález bez `file` existuje (findingy z toolů, ne ze souboru) — bez fallbacku
  // spadla celá stránka /core/toolbox (error_boundary 2026-08-06 09:38).
  const file = f.file ?? '';
  if (file.includes('/')) return file.split('/')[0] ?? file;
  return file || 'acm-core';
}

function scopeCountsFromTargets(targets?: string[]): Record<SecurityScope, number> {
  const counts: Record<SecurityScope, number> = { core: 0, module: 0, hosted: 0 };
  for (const target of targets ?? []) counts[scopeForTarget(target)]++;
  return counts;
}

function groupedFindings(findings: SecurityFinding[]): Record<SecurityScope, SecurityFinding[]> {
  const groups: Record<SecurityScope, SecurityFinding[]> = { core: [], module: [], hosted: [] };
  for (const f of findings) groups[scopeForTarget(targetFromFinding(f))].push(f);
  for (const key of Object.keys(groups) as SecurityScope[]) {
    groups[key].sort((a, b) => {
      const sevOrder: Record<string, number> = { high: 0, med: 1, low: 2 };
      return (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9) ||
        (a.file ?? '').localeCompare(b.file ?? '') ||
        (a.line ?? 0) - (b.line ?? 0);
    });
  }
  return groups;
}

function ScopeTargetBadges({ targets }: { targets?: string[] }) {
  const { t } = useTranslation();
  const counts = scopeCountsFromTargets(targets);
  const visible = (['core', 'module', 'hosted'] as SecurityScope[])
    .filter((scope) => counts[scope] > 0);
  if (visible.length === 0) return null;
  return (
    <div className='flex items-center gap-1 mt-1'>
      {visible.map((scope) => (
        <Badge key={scope} variant={SECURITY_SCOPE_META[scope].badge} className='text-[10px]'>
          {t(SECURITY_SCOPE_META[scope].labelKey, SECURITY_SCOPE_META[scope].label)}: {counts[scope]}
        </Badge>
      ))}
    </div>
  );
}

// ─── Health Tab ─────────────────────────────────────────────────────────────

function HealthTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['toolbox-health'],
    queryFn: () =>
      callMcp<
        {
          ok: boolean;
          status: string;
          tests?: Array<{ name: string; status: string; duration_ms: number; message?: string }>;
          ts?: string;
          id?: string;
        }
      >('toolbox.health_get', {}),
    staleTime: 30_000,
  });
  const { data: histData } = useQuery({
    queryKey: ['toolbox-health-history'],
    queryFn: () =>
      callMcp<{ history: Array<{ id: string; ts: string; status: string; duration_ms: number }> }>(
        'toolbox.health_history_get',
        {},
      ),
    staleTime: 60_000,
  });

  const runMut = useMutation({
    mutationFn: () => callMcp('toolbox.health_monitor', { full: true }),
    onSuccess: () => {
      toast.success(t('toolbox.healthCheckDokoncen'));
      qc.invalidateQueries({ queryKey: ['toolbox-health'] });
      qc.invalidateQueries({ queryKey: ['toolbox-health-history'] });
    },
  });

  const tests = data?.tests ?? [];
  const history = histData?.history ?? [];
  const okCount = tests.filter((t) => t.status === 'ok').length;
  const warnCount = tests.filter((t) => t.status === 'warning').length;
  const errCount = tests.filter((t) => t.status === 'error').length;

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='text-sm text-gray-500'>
          {data?.ts
            ? t('toolbox.lastCheckAt', 'Poslední check: {{when}} ({{ago}})', {
              when: new Date(data.ts).toLocaleString(),
              ago: timeAgo(data.ts),
            })
            : t('toolbox.noCheck', 'Žádný check')}
        </div>
        <Button
          variant='primary'
          size='sm'
          onClick={() => runMut.mutate()}
          disabled={runMut.isPending}
          icon={runMut.isPending
            ? <Loader2 size={14} className='animate-spin' />
            : <Play size={14} />}
        >
          {t('toolbox.spustitCheck')}
        </Button>
      </div>

      {tests.length > 0 && (
        <StatsGrid columns={4}>
          <StatCard label='Celkem' value={tests.length} icon={Activity} />
          <StatCard label='OK' value={okCount} icon={CheckCircle2} variant='success' />
          <StatCard
            label={t('toolbox.varovani')}
            value={warnCount}
            icon={AlertTriangle}
            variant={warnCount > 0 ? 'warning' : 'default'}
          />
          <StatCard
            label={t('toolbox.chyba', 'Chyba')}
            value={errCount}
            icon={XCircle}
            variant={errCount > 0 ? 'error' : 'default'}
          />
        </StatsGrid>
      )}

      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : tests.length === 0
        ? (
          <EmptyState
            icon={<Activity size={24} />}
            title={t('toolbox.zadnyHealthCheck')}
            description={t('toolbox.spusttePrvniKontroluTlacitkem')}
          />
        )
        : (
          <Card>
            <CardContent>
              <div className='divide-y divide-gray-800'>
                {tests.map((t, i) => (
                  <div key={i} className='flex items-center gap-3 py-2.5'>
                    <StatusDot
                      status={t.status === 'ok'
                        ? 'running'
                        : t.status === 'warning'
                        ? 'pending'
                        : 'error'}
                    />
                    <span className='font-mono text-sm text-white flex-1'>{t.name}</span>
                    {t.message && <span className='text-xs text-gray-500'>{t.message}</span>}
                    <span className='text-xs text-gray-600 tabular-nums'>{t.duration_ms}ms</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

      <TaskScheduler toolFilter='health' defaultTool='health_monitor' defaultModule='toolbox' />

      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle icon={Clock}>Historie ({history.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-1 max-h-48 overflow-y-auto'>
              {history.map((h) => (
                <div key={h.id} className='flex items-center gap-3 text-xs py-1'>
                  <StatusDot
                    status={h.status === 'ok'
                      ? 'running'
                      : h.status === 'warning'
                      ? 'pending'
                      : 'error'}
                  />
                  <span className='text-gray-500'>{new Date(h.ts).toLocaleString('cs')}</span>
                  <span className='flex-1' />
                  <span className='text-gray-600 tabular-nums'>{h.duration_ms}ms</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Security Tab ───────────────────────────────────────────────────────────

function SecurityTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [detail, setDetail] = useState<string | null>(null);

  const { data: scansData, isLoading } = useQuery({
    queryKey: ['toolbox-scans'],
    queryFn: () =>
      callMcp<
        {
          scans: Array<
            {
              id: string;
              started_at: string;
              finished_at: string;
              status: string;
              target: string;
              summary: SecurityScanSummary;
            }
          >;
        }
      >('toolbox.scans_list', {}),
  });

  const { data: detailData } = useQuery({
    queryKey: ['toolbox-scan-detail', detail],
    queryFn: () =>
      detail
        ? callMcp<
          {
            scan: {
              findings: SecurityFinding[];
            };
          }
        >('toolbox.scan_get', { id: detail })
        : null,
    enabled: !!detail,
  });

  const runScan = useMutation({
    mutationFn: () => callMcp('toolbox.scan_start', {}),
    onSuccess: () => {
      toast.success(t('toolbox.scanSpusten'));
      qc.invalidateQueries({ queryKey: ['toolbox-scans'] });
    },
  });

  const scans = scansData?.scans ?? [];
  const findings = detailData?.scan?.findings ?? [];
  const findingGroups = groupedFindings(findings);

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='text-sm text-gray-500'>{t('toolbox.scansCount', '{{count}} scanů', { count: scans.length })}</div>
        <Button
          variant='primary'
          size='sm'
          onClick={() => runScan.mutate()}
          disabled={runScan.isPending}
          icon={runScan.isPending
            ? <Loader2 size={14} className='animate-spin' />
            : <Shield size={14} />}
        >
          {t('toolbox.novyScan')}
        </Button>
      </div>

      <TaskScheduler toolFilter='security' defaultTool='security_scan' defaultModule='toolbox' />

      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : scans.length === 0
        ? (
          <EmptyState
            icon={<Shield size={24} />}
            title={t('toolbox.zadneScany')}
            description={t('toolbox.spusttePrvniSecurityScan')}
          />
        )
        : (
          <div className='space-y-2'>
            {scans.map((s) => (
              <Card key={s.id} className={detail === s.id ? 'border-blue-500/40' : ''}>
                <CardContent className='py-3'>
                  <div
                    className='flex items-center gap-3 cursor-pointer'
                    onClick={() =>
                      setDetail(detail === s.id ? null : s.id)}
                  >
                    <StatusDot
                      status={s.status !== 'done'
                        ? 'pending'
                        : ((s.summary?.high ?? 0) > 0 || (s.summary?.critical ?? 0) > 0)
                        ? 'error'
                        : (s.summary?.med ?? 0) > 0
                        ? 'pending'
                        : 'running'}
                    />
                    <div className='flex-1 min-w-0'>
                      <span className='font-mono text-xs text-gray-400'>{s.id.slice(0, 8)}</span>
                      <span className='text-gray-600 text-xs ml-2'>{timeAgo(s.started_at)}</span>
                      <span className='text-gray-700 text-xs ml-2'>{s.target}</span>
                      <ScopeTargetBadges targets={s.summary.targets} />
                      {s.summary.password_leaks && (
                        <div className='text-xs text-gray-500 mt-1'>
                          {t('toolbox.passwordLeakCheck', 'Hesla: ověřeno {{checked}}, nálezy {{exposed}}', s.summary.password_leaks)}
                          {s.summary.password_leaks.status === 'incomplete' && (
                            <span className='text-amber-400 ml-2'>
                              {t('toolbox.passwordLeakIncomplete', 'Kontrola není úplná')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className='flex items-center gap-1.5'>
                      {(s.summary.high ?? 0) > 0 && <Badge variant='red'>{s.summary.high} HIGH</Badge>}
                      {(s.summary.med ?? 0) > 0 && <Badge variant='yellow'>{s.summary.med} MED</Badge>}
                      {(s.summary.low ?? 0) > 0 && <Badge>{s.summary.low} LOW</Badge>}
                      {!s.summary.high && !s.summary.med && !s.summary.low && (
                        <Badge variant='green'>Clean</Badge>
                      )}
                    </div>
                    {detail === s.id
                      ? <ChevronDown size={14} className='text-gray-600' />
                      : <ChevronRight size={14} className='text-gray-600' />}
                  </div>

                  {detail === s.id && findings.length > 0 && (
                    <div className='mt-3 pt-3 border-t border-gray-800 space-y-3'>
                      {(['core', 'module', 'hosted'] as SecurityScope[]).map((scope) => {
                        const scoped = findingGroups[scope];
                        if (scoped.length === 0) {
                          return null;
                        }
                        const meta = SECURITY_SCOPE_META[scope];
                        return (
                          <div key={scope}>
                            <div className='flex items-center gap-2 mb-1.5'>
                              <Badge variant={meta.badge}>{t(meta.labelKey, meta.label)}</Badge>
                              <span className='text-xs text-gray-500'>
                                {t('toolbox.findingsCount', '{{count}} nálezů', { count: scoped.length })}
                              </span>
                            </div>
                            {scoped.slice(0, scope === 'hosted' ? 30 : 20).map((f, i) => (
                              <FindingRow key={`${scope}-${i}`} f={f} />
                            ))}
                            {scoped.length > (scope === 'hosted' ? 30 : 20) && (
                              <p className='text-[10px] text-gray-600'>
                                {t('toolbox.andMoreFindings', '... a {{count}} dalších', {
                                  count: scoped.length - (scope === 'hosted' ? 30 : 20),
                                })}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

function FindingRow(
  { f }: {
    f: {
      file: string;
      line: number;
      message: string;
      severity: string;
      category: string;
      content: string;
    };
  },
) {
  const [open, setOpen] = useState(false);
  return (
    <div className='border border-gray-800 rounded-lg overflow-hidden mb-1'>
      <div
        className='flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-800/30'
        onClick={() => setOpen(!open)}
      >
        <span
          className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
            SEV_COLOR[f.severity] ?? SEV_COLOR.low
          }`}
        >
          {f.severity.toUpperCase()}
        </span>
        <div className='flex-1 min-w-0'>
          <p className='text-xs text-white'>{f.message}</p>
          <p className='text-[10px] text-gray-600 font-mono'>{f.file}:{f.line} · {f.category}</p>
        </div>
        {open
          ? <ChevronDown size={12} className='text-gray-600 mt-0.5' />
          : <ChevronRight size={12} className='text-gray-600 mt-0.5' />}
      </div>
      {open && (
        <pre className='px-3 py-2 bg-gray-900/50 text-[10px] font-mono text-gray-400 border-t border-gray-800 overflow-x-auto'>{f.content}</pre>
      )}
    </div>
  );
}

// ─── Updates Tab (widget panel) ────────────────────────────

// Modulová záložka Updates JAKO widget panel se zamčnutým defaultem (ModulePanel).
// CO/JAK se updatuje, živý progress i restart prompt žijí ve widgetu
// `toolbox.sysupdate` (widgets/registry.toolbox.tsx). Bývalý bespoke
// UpdatesTab + OsUpdateSection nahrazeny 2026-07-22 (module UI = locked panel).

// ─── Backup Tab ─────────────────────────────────────────────────────────────

// BackupTab — kompletní: zálohy, snapshoty, module bundle, TTL, cloud sync, plánování

function BackupTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selMod, setSelMod] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState<'container' | 'module'>('module');
  const [containerName, setContainerName] = useState('');
  const [cloudSync, setCloudSync] = useState('');

  // Data queries
  const { data: backups, isLoading: loadingB } = useQuery({
    queryKey: ['toolbox-backups'],
    queryFn: () => callMcp('toolbox.backup_list', {}),
  });

  const { data: snaps } = useQuery({
    queryKey: ['toolbox-snapshots'],
    queryFn: () => callMcp('toolbox.snapshot_list', {}),
  });

  const { data: modsData } = useQuery({
    queryKey: ['mods-for-backup'],
    queryFn: () => callMcp<{ modules: Array<{ name: string }> }>('acm-self.modules_list'),
  });

  // Mutations
  const bundleMut = useMutation({
    mutationFn: (opts: { module: string; cloud_sync?: string }) =>
      callMcp('toolbox.backup_module', opts),
    onSuccess: () => {
      toast.success(t('toolbox.moduleBundleVytvoren'));
      qc.invalidateQueries({ queryKey: ['toolbox-backups'] });
      setShowCreate(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const containerMut = useMutation({
    mutationFn: (container: string) => callMcp('toolbox.backup_create', { container }),
    onSuccess: () => {
      toast.success(t('toolbox.zalohaVytvorena'));
      qc.invalidateQueries({ queryKey: ['toolbox-backups'] });
      setShowCreate(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (name: string) => callMcp('toolbox.backup_delete', { backup: name }),
    onSuccess: () => {
      toast.success(t('toolbox.smazano'));
      qc.invalidateQueries({ queryKey: ['toolbox-backups'] });
    },
  });

  const restoreMut = useMutation({
    mutationFn: (name: string) => callMcp('toolbox.backup_restore', { backup: name }),
    onSuccess: (r: unknown) => {
      const preBackup = (r as { pre_restore_backup?: string })?.pre_restore_backup ?? null;
      toast.success(
        preBackup
          ? t('toolbox.restoreOkPre', 'Obnoveno — pre-restore záloha: {{pre}}. Restartujte kontejner.', { pre: preBackup })
          : t('toolbox.restoreOkNoPre', 'Obnoveno — restartujte kontejner (pre-restore backup se nepodařil)'),
      );
      qc.invalidateQueries({ queryKey: ['toolbox-backups'] });
    },
    onError: (e: Error) => toast.error(`Restore selhal: ${e.message}`),
  });

  const bList = (backups as { backups?: Array<Record<string, string>> })?.backups ?? [];
  const sList = (snaps as { snapshots?: Array<Record<string, string>> })?.snapshots ?? [];
  const modules = modsData?.modules ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between flex-wrap gap-2'>
        <StatsGrid columns={3}>
          <StatCard label={t('toolbox.zalohy')} value={bList.length} icon={Archive} />
          <StatCard label='Snapshoty' value={sList.length} icon={HardDrive} />
          <StatCard label={t('toolbox.umisteni')} value='/var/acm/backups' icon={HardDrive} />
        </StatsGrid>
      </div>

      <div className='flex items-center gap-2 flex-wrap'>
        <Button
          variant='primary'
          size='sm'
          onClick={() => setShowCreate(true)}
          icon={<Archive size={14} />}
        >
          {t('toolbox.novaZaloha')}
        </Button>

        <Button
          variant='secondary'
          size='sm'
          onClick={() => qc.invalidateQueries({ queryKey: ['toolbox-backups'] })}
          icon={<RefreshCw size={14} />}
        >
          Refresh
        </Button>
      </div>

      {/* Create modal */}
      {showCreate && (
        <Card className='border-blue-500/30'>
          <CardHeader>
            <CardTitle>{t('toolbox.novaZaloha')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              <div className='flex gap-2'>
                <button
                  onClick={() => setCreateType('module')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    createType === 'module' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                  }`}
                >
                  {t('toolbox.moduleBundle')}
                </button>
                <button
                  onClick={() => setCreateType('container')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    createType === 'container'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-400'
                  }`}
                >
                  Kontejner
                </button>
              </div>

              {createType === 'module'
                ? (
                  <div className='space-y-2'>
                    <Select
                      value={selMod}
                      onChange={(e) => setSelMod(e.target.value)}
                      options={modules.map((m) => ({ value: m.name, label: m.name }))}
                      placeholder={t('toolbox.vyberModul')}
                    />
                    <p className='text-[10px] text-gray-600'>
                      {t('toolbox.moduleBundleKontejneryVolumes')}
                    </p>
                  </div>
                )
                : (
                  <div className='space-y-2'>
                    <input
                      value={containerName}
                      onChange={(e) => setContainerName(e.target.value)}
                      placeholder={t('toolbox.nazevKontejneruAcmDb')}
                      className='w-full h-9 px-3 text-sm rounded-xl border bg-gray-900/40 text-white focus:outline-none focus:border-blue-500'
                    />
                  </div>
                )}

              <div className='space-y-2'>
                <label className='text-xs text-gray-500'>{t('toolbox.cloudSyncVolitelne')}</label>
                <input
                  value={cloudSync}
                  onChange={(e) => setCloudSync(e.target.value)}
                  placeholder={t('toolbox.idDatasyncZdrojeS3')}
                  className='w-full h-9 px-3 text-sm rounded-xl border bg-gray-900/40 text-white focus:outline-none focus:border-blue-500'
                />
                <p className='text-[10px] text-gray-600'>
                  {t('toolbox.poZalozeNahrajeNa')}
                </p>
              </div>

              <div className='flex gap-2 pt-2 border-t border-gray-800'>
                <Button variant='secondary' size='sm' onClick={() => setShowCreate(false)}>
                  {t('toolbox.zrusit')}
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  disabled={createType === 'module' ? !selMod : !containerName}
                  onClick={() => {
                    if (createType === 'module') {
                      bundleMut.mutate({ module: selMod, cloud_sync: cloudSync || undefined });
                    } else containerMut.mutate(containerName);
                  }}
                  icon={bundleMut.isPending || containerMut.isPending
                    ? <Loader2 size={14} className='animate-spin' />
                    : <Archive size={14} />}
                >
                  {t('toolbox.vytvorit')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Naplánované zálohy */}
      <TaskScheduler toolFilter='backup' defaultTool='backup_module' defaultModule='toolbox' />

      {/* Zálohy */}
      {loadingB
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : bList.length === 0 && sList.length === 0
        ? (
          <EmptyState
            icon={<Archive size={24} />}
            title={t('toolbox.zadneZalohy')}
            description={t('toolbox.vytvortePrvniZalohuTlacitkem')}
          />
        )
        : (
          <>
            {bList.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle icon={Archive}>{t('toolbox.zalohy')} ({bList.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-2.5'>
                    {bList.map((b, i) => (
                      <div
                        key={i}
                        className='rounded-2xl border border-gray-800 bg-gray-950/50 p-3 flex flex-col gap-2 hover:border-gray-700 transition-colors'
                      >
                        <div className='flex items-start gap-2.5 min-w-0'>
                          <div className='rounded-xl p-2 bg-emerald-500/10 text-emerald-400 shrink-0'>
                            <Archive size={14} />
                          </div>
                          <div className='min-w-0 flex-1'>
                            <div className='font-mono text-xs text-white break-all leading-snug'>{b.name}</div>
                            <div className='text-[11px] text-gray-500 mt-0.5 truncate'>
                              {b.container || '—'}
                            </div>
                          </div>
                        </div>
                        <div className='flex flex-wrap items-center gap-2 text-[11px] text-gray-500'>
                          {b.size && <Badge variant='gray' className='text-[10px]'>{b.size}</Badge>}
                          {b.created_at && (
                            <span className='font-mono'>{timeAgo(b.created_at)}</span>
                          )}
                        </div>
                        <div className='flex flex-wrap gap-1.5 pt-1.5 border-t border-gray-800/80'>
                          <Button
                            variant='secondary'
                            size='sm'
                            onClick={async () => {
                              if (
                                await themedConfirm(
                                  t('toolbox.restoreConfirm', {
                                    name: b.name,
                                    container: b.container || '?',
                                  }),
                                )
                              ) {
                                restoreMut.mutate(b.name);
                              }
                            }}
                            icon={<Download size={12} />}
                          >
                            {t('toolbox.obnovit', 'Obnovit')}
                          </Button>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='ml-auto text-red-400/80'
                            onClick={async () => {
                              if (await themedConfirm(t('toolbox.smazatZalohuConfirm', 'Smazat zálohu?'))) {
                                deleteMut.mutate(b.name);
                              }
                            }}
                            icon={<Trash2 size={12} />}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {sList.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle icon={HardDrive}>{t('toolbox.snapshoty', 'Snapshoty')} ({sList.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                    {sList.map((s, i) => (
                      <div
                        key={i}
                        className='rounded-xl border border-gray-800 bg-gray-950/40 px-3 py-2.5 flex items-center justify-between gap-2'
                      >
                        <div className='min-w-0'>
                          <div className='font-mono text-xs text-white truncate'>{s.name}</div>
                          <div className='text-[10px] text-gray-500 truncate'>{s.container}</div>
                        </div>
                        <span className='text-[10px] text-gray-600 shrink-0 font-mono'>
                          {s.created_at ? timeAgo(s.created_at) : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
    </div>
  );
}

// ─── Cleaner Tab ────────────────────────────────────────────────────────────

// CleanerTab — scan → select → clean flow
function CleanerTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [scanResults, setScanResults] = useState<
    Array<
      { name: string; path?: string; freed_bytes: number; items_removed: number; error?: string }
    > | null
  >(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Historie běhů
  const { data, isLoading } = useQuery({
    queryKey: ['toolbox-cleaner'],
    queryFn: () =>
      callMcp<
        {
          ok: boolean;
          runs: Array<
            {
              id: string;
              ts: string;
              freed_bytes: number;
              targets: Array<
                { name: string; path: string; freed_bytes: number; items_removed: number }
              >;
              duration_ms: number;
            }
          >;
        }
      >('toolbox.cleaner_history', {}),
  });

  // Dry run scan
  const scanMut = useMutation({
    mutationFn: () =>
      callMcp<Record<string, unknown>>('toolbox.cleaner_run', { dry_run: true }),
    onSuccess: (d: Record<string, unknown>) => {
      const targets =
        (d.targets as Array<{ name: string; freed_bytes: number; items_removed: number }>) ?? [];
      setScanResults(targets);
      // Select all by default
      setSelected(new Set(targets.map((t) => t.name)));
    },
  });

  // Real clean
  const cleanMut = useMutation({
    mutationFn: () =>
      callMcp('toolbox.cleaner_run', { dry_run: false, targets: Array.from(selected) }),
    onSuccess: () => {
      toast.success(t('toolbox.cisteniDokonceno'));
      setScanResults(null);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['toolbox-cleaner'] });
    },
  });

  const toggleTarget = (name: string) => {
    const next = new Set(selected);
    next.has(name) ? next.delete(name) : next.add(name);
    setSelected(next);
  };

  const runs = data?.runs ?? [];
  const totalFreed = runs.reduce((s, r) => s + r.freed_bytes, 0);

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='text-sm text-gray-500'>
          {runs.length} {t('toolbox.behuCelkemUvolneno', 'běhů, celkem uvolněno')} {formatBytes(totalFreed)}
        </div>
        <div className='flex gap-2'>
          {!scanResults && (
            <Button
              variant='primary'
              size='sm'
              onClick={() => scanMut.mutate()}
              disabled={scanMut.isPending}
              icon={scanMut.isPending
                ? <Loader2 size={14} className='animate-spin' />
                : <Eye size={14} />}
            >
              {t('toolbox.scanCoVycistit')}
            </Button>
          )}
          {scanResults && (
            <>
              <Button
                variant='secondary'
                size='sm'
                onClick={() => {
                  setScanResults(null);
                  setSelected(new Set());
                }}
              >
                {t('toolbox.zrusit')}
              </Button>
              <Button
                variant='primary'
                size='sm'
                onClick={() => cleanMut.mutate()}
                disabled={selected.size === 0 || cleanMut.isPending}
                icon={cleanMut.isPending
                  ? <Loader2 size={14} className='animate-spin' />
                  : <Trash2 size={14} />}
              >
                {t('toolbox.vycistitVybrane', 'Vyčistit vybrané ({{count}})', { count: selected.size })}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Scan results s checkboxy */}
      {scanResults && (
        <Card>
          <CardHeader>
            <CardTitle icon={Eye}>{t('toolbox.nalezenoKVycisteni')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='divide-y divide-gray-800'>
              {scanResults.map((res) => (
                <label
                  key={res.name}
                  className='flex items-center gap-3 py-2.5 cursor-pointer hover:bg-gray-800/20 px-1 rounded'
                >
                  <input
                    type='checkbox'
                    checked={selected.has(res.name)}
                    onChange={() => toggleTarget(res.name)}
                    className='rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500'
                  />
                  <div className='flex-1'>
                    <span className='text-sm text-white font-medium'>
                      {res.name.replace(/_/g, ' ')}
                    </span>
                    {res.path && (
                      <span className='text-[10px] text-gray-600 font-mono ml-2'>{res.path}</span>
                    )}
                  </div>
                  <div className='flex items-center gap-2 text-xs'>
                    {res.items_removed > 0 && (
                      <span className='text-gray-400'>{res.items_removed} {t('toolbox.polozek', 'položek')}</span>
                    )}
                    {res.freed_bytes > 0
                      ? <Badge variant='success'>{formatBytes(res.freed_bytes)}</Badge>
                      : <Badge>{t('toolbox.0B')}</Badge>}
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <TaskScheduler toolFilter='cleaner' defaultTool='cleaner_run' defaultModule='toolbox' />

      <UnfinishedProjects key={`${getToolboxWidgetScope().node}:${getToolboxWidgetScope().identity}`} scope={getToolboxWidgetScope()} />

      {/* Historie */}
      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : runs.length === 0 && !scanResults
        ? (
          <EmptyState
            icon={<Trash2 size={24} />}
            title={t('toolbox.zadnaHistorie')}
            description={t('toolbox.spustteScanAVyberte')}
          />
        )
        : runs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle icon={Clock}>Historie ({runs.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='divide-y divide-gray-800'>
                {runs.map((r) => (
                  <div key={r.id} className='py-2.5'>
                    <div className='flex items-center justify-between mb-1'>
                      <span className='text-xs text-gray-500'>
                        {new Date(r.ts).toLocaleString('cs')}
                      </span>
                      <div className='flex items-center gap-2'>
                        <Badge variant={r.freed_bytes > 0 ? 'success' : 'default'}>
                          {formatBytes(r.freed_bytes)}
                        </Badge>
                        <span className='text-[10px] text-gray-600'>{r.duration_ms}ms</span>
                      </div>
                    </div>
                    {r.targets?.length > 0 && (
                      <div className='ml-4 space-y-0.5'>
                        {r.targets.filter((t) => t.items_removed > 0 || t.freed_bytes > 0).map((
                          t,
                          i,
                        ) => (
                          <div key={i} className='flex items-center gap-2 text-[10px]'>
                            <CheckCircle2 size={10} className='text-emerald-500' />
                            <span className='text-gray-400'>{t.name}</span>
                            {t.items_removed > 0 && (
                              <span className='text-gray-500'>{t.items_removed}×</span>
                            )}
                            {t.freed_bytes > 0 && (
                              <span className='text-blue-400'>{formatBytes(t.freed_bytes)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
}
// WatchdogTab — hlídání služeb, inhibit, historie
function WatchdogTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [inhibitSvc, setInhibitSvc] = useState('');
  const [inhibitReason, setInhibitReason] = useState('');
  const [inhibitHours, setInhibitHours] = useState(1);

  // Spustit watchdog manuálně
  const runMut = useMutation({
    mutationFn: () => callMcp('toolbox.sysupdate_watchdog', { services: WATCHED_SERVICES }),
    onSuccess: () => {
      toast.success(t('toolbox.watchdogCheckDokoncen'));
      qc.invalidateQueries({ queryKey: ['watchdog-status'] });
    },
  });

  // Status — poslední run
  const { data: statusData, isLoading } = useQuery({
    queryKey: ['watchdog-status'],
    queryFn: () => callMcp('toolbox.sysupdate_watchdog', { services: WATCHED_SERVICES }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Inhibit service
  const inhibitMut = useMutation({
    mutationFn: (opts: { service: string; reason: string; hours: number }) =>
      callMcp('acm-self.module_settings', {
        action: 'set_sys',
        module: 'sysupdate',
        patch: {
          [`watchdog_inhibit_${opts.service}`]: {
            reason: opts.reason,
            until: new Date(Date.now() + opts.hours * 3600000).toISOString(),
            set_by: 'ui',
          },
        },
      }),
    onSuccess: () => {
      toast.success(t('toolbox.inhibitNastaven'));
      setInhibitSvc('');
      qc.invalidateQueries({ queryKey: ['watchdog-status'] });
    },
  });

  const results = (statusData as {
    results?: Record<
      string,
      { status: string; inhibited: boolean; inhibit_reason?: string; action: string; ok: boolean }
    >;
  })?.results ?? {};
  const checkedAt = (statusData as { checked_at?: string })?.checked_at;

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='text-sm text-gray-500'>
          {checkedAt
            ? t('toolbox.lastCheckSimple', 'Poslední check: {{when}}', { when: new Date(checkedAt).toLocaleString() })
            : t('common.loading', 'Načítám…')}
        </div>
        <Button
          variant='primary'
          size='sm'
          onClick={() => runMut.mutate()}
          disabled={runMut.isPending}
          icon={runMut.isPending
            ? <Loader2 size={14} className='animate-spin' />
            : <Eye size={14} />}
        >
          {t('toolbox.checkTed')}
        </Button>
      </div>

      {/* Služby */}
      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : (
          <Card>
            <CardHeader>
              <CardTitle icon={Server}>{t('toolbox.hlidaneSluzby')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='divide-y divide-gray-800'>
                {Object.entries(results).map(([svc, r]) => (
                  <div key={svc} className='flex items-center gap-3 py-2.5'>
                    <StatusDot status={r.ok ? 'running' : r.inhibited ? 'pending' : 'error'} />
                    <span className='font-mono text-sm text-white flex-1'>{svc}</span>
                    <Badge variant={r.ok ? 'success' : r.inhibited ? 'warning' : 'error'}>
                      {r.ok ? 'OK' : r.status}
                    </Badge>
                    {r.inhibited && <Badge variant='warning'>INHIBIT: {r.inhibit_reason}</Badge>}
                    {r.action !== 'none' && r.action !== 'skipped' && (
                      <Badge variant={r.ok ? 'success' : 'error'}>{r.action}</Badge>
                    )}
                    <Button
                      variant='secondary'
                      size='sm'
                      onClick={() =>
                        setInhibitSvc(inhibitSvc === svc ? '' : svc)}
                    >
                      {inhibitSvc === svc ? t('toolbox.zrusit', 'Zrušit') : 'Inhibit'}
                    </Button>
                  </div>
                ))}
                {Object.keys(results).length === 0 && (
                  <p className='text-xs text-gray-600 py-2'>
                    {t('toolbox.zadneSluzbyWatchdogTask')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

      {/* Inhibit formulář */}
      {inhibitSvc && (
        <Card className='border-amber-500/30'>
          <CardHeader>
            <CardTitle icon={AlertTriangle}>Inhibit: {inhibitSvc}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-2'>
              <input
                value={inhibitReason}
                onChange={(e) => setInhibitReason(e.target.value)}
                placeholder={t('toolbox.duvodMaintenanceDeploy')}
                className='w-full h-8 px-3 text-xs rounded-xl border bg-gray-900/40 text-white'
              />
              <div className='flex items-center gap-2'>
                <span className='text-xs text-gray-500'>{t('toolbox.na')}</span>
                {[1, 2, 4, 8, 24].map((h) => (
                  <button
                    key={h}
                    onClick={() => setInhibitHours(h)}
                    className={`px-2 py-1 rounded text-xs ${
                      inhibitHours === h ? 'bg-amber-600 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
              <Button
                variant='warning'
                size='sm'
                disabled={!inhibitReason}
                onClick={() =>
                  inhibitMut.mutate({
                    service: inhibitSvc,
                    reason: inhibitReason,
                    hours: inhibitHours,
                  })}
              >
                Nastavit inhibit ({inhibitHours}h)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plánovaný watchdog task */}
      <TaskScheduler
        toolFilter='watchdog'
        defaultTool='sysupdate_watchdog'
        defaultModule='toolbox'
      />
    </div>
  );
}

const WATCHED_SERVICES = ['acm-api', 'acm-dashboard', 'acm-db', 'emqx'];

// CrashRecoveryTab — UI ErrorBoundary crash log + auto-recovery info.
// POZOR: nezaměňovat se self-defence modulem (= /modules/self-defence)
// který řeší IP blacklist/whitelist/security log. Tady jde o React UI
// chyby (ErrorBoundary catch) + audit Loki logging recovery info.
function CrashRecoveryTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['toolbox-ui-crashes'],
    queryFn: () =>
      callMcp<
        {
          entries: Array<
            {
              ts: string;
              data: { message: string; url: string; stack?: string; component_stack?: string };
              ip: string;
            }
          >;
        }
      >('acm-self.audit_log', { category: 'ui_crash', limit: 50 }),
    staleTime: 15_000,
  });

  const crashes = data?.entries ?? [];
  const last24h = crashes.filter((c) => Date.now() - new Date(c.ts).getTime() < 86400000);

  return (
    <div className='space-y-4'>
      <StatsGrid columns={3}>
        <StatCard
          label={t('toolbox.crashCelkem')}
          value={crashes.length}
          icon={AlertTriangle}
          variant={crashes.length > 0 ? 'error' : 'default'}
        />
        <StatCard
          label={t('toolbox.poslednich24h')}
          value={last24h.length}
          icon={Clock}
          variant={last24h.length > 0 ? 'warning' : 'success'}
        />
        <StatCard label={t('toolbox.autoRetry')} value='30s' icon={Shield} variant='info' />
      </StatsGrid>

      <Card>
        <CardHeader>
          <CardTitle icon={Shield}>{t('toolbox.uiCrashRecovery')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-3 text-sm'>
            <div className='flex items-center gap-3 py-2 border-b border-gray-800'>
              <StatusDot status='running' />
              <span className='flex-1 text-white'>ErrorBoundary</span>
              <Badge variant='success'>{t('toolbox.aktivni')}</Badge>
            </div>
            <div className='flex items-center gap-3 py-2 border-b border-gray-800'>
              <StatusDot status='running' />
              <span className='flex-1 text-white'>{t('toolbox.auditLokiLogging')}</span>
              <Badge variant='success'>{t('toolbox.aktivni')}</Badge>
            </div>
            <div className='flex items-center gap-3 py-2 border-b border-gray-800'>
              <StatusDot status='running' />
              <span className='flex-1 text-white'>{t('toolbox.autoRetry30s')}</span>
              <Badge variant='info'>30s</Badge>
            </div>
            <div className='flex items-center gap-3 py-2'>
              <StatusDot status='running' />
              <span className='flex-1 text-white'>{t('toolbox.perModuleIsolation')}</span>
              <Badge variant='success'>{t('toolbox.aktivni')}</Badge>
            </div>
          </div>
          <p className='text-[10px] text-gray-600 mt-3'>
            {t(
              'toolbox.errorBoundaryHint',
              'Každý modul je zabalený v ErrorBoundary. Crash jednoho modulu neovlivní ostatní. Chyby se automaticky logují do Audit Loki → agent je najde a opraví.',
            )}
          </p>
        </CardContent>
      </Card>

      {/* UI Crash log */}
      <Card>
        <CardHeader>
          <CardTitle icon={AlertTriangle}>UI Crash Log ({crashes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading
            ? (
              <div className='flex justify-center py-4'>
                <Loader2 size={14} className='animate-spin text-gray-600' />
              </div>
            )
            : crashes.length === 0
            ? (
              <div className='text-center py-6'>
                <CheckCircle2 size={24} className='mx-auto text-emerald-500 mb-2' />
                <p className='text-sm text-gray-400'>{t('toolbox.zadneUiCrashe')}</p>
                <p className='text-[10px] text-gray-600'>{t('toolbox.vsechnoBeziBezChyb')}</p>
              </div>
            )
            : (
              <div className='divide-y divide-gray-800 max-h-96 overflow-y-auto'>
                {crashes.map((c, i) => <CrashRow key={i} crash={c} />)}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

function CrashRow(
  { crash }: {
    crash: {
      ts: string;
      data: { message: string; url: string; stack?: string; component_stack?: string };
      ip: string;
    };
  },
) {
  const [expanded, setExpanded] = useState(false);
  const d = crash.data ?? {};
  return (
    <div className='py-2'>
      <div className='flex items-start gap-2 cursor-pointer' onClick={() => setExpanded(!expanded)}>
        <XCircle size={14} className='text-red-400 mt-0.5 flex-shrink-0' />
        <div className='flex-1 min-w-0'>
          <p className='text-xs text-red-300 font-mono truncate'>{d.message}</p>
          <p className='text-[10px] text-gray-600'>
            {d.url} · {new Date(crash.ts).toLocaleString('cs')}
          </p>
        </div>
        {expanded
          ? <ChevronDown size={12} className='text-gray-600' />
          : <ChevronRight size={12} className='text-gray-600' />}
      </div>
      {expanded && (
        <div className='ml-6 mt-1 space-y-1'>
          {d.stack && (
            <pre className='text-[9px] font-mono text-gray-600 bg-gray-900/50 rounded-lg p-2 overflow-x-auto max-h-32'>{d.stack}</pre>
          )}
          {d.component_stack && (
            <pre className='text-[9px] font-mono text-blue-400/50 bg-gray-900/50 rounded-lg p-2 overflow-x-auto max-h-24'>{d.component_stack}</pre>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

// ─── Hosts Editor (/etc/hosts) ───────────────────────────────────────────────

function HostsEditor() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['toolbox-hosts'],
    queryFn: () =>
      callMcp<
        {
          ok: boolean;
          content: string;
          entries: Array<
            { line: number; ip?: string; hostnames?: string[]; raw: string; comment: boolean }
          >;
        }
      >('toolbox.hosts_get', {}),
    staleTime: 30_000,
  });

  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [newIp, setNewIp] = useState('');
  const [newHostname, setNewHostname] = useState('');

  const saveMut = useMutation({
    mutationFn: (content: string) => callMcp('toolbox.hosts_set', { content }),
    onSuccess: () => {
      toast.success(t('toolbox.hostsUlozen'));
      setEditMode(false);
      qc.invalidateQueries({ queryKey: ['toolbox-hosts'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const addMut = useMutation({
    mutationFn: (entry: { ip: string; hostname: string }) =>
      callMcp('toolbox.hosts_entry_add', entry),
    onSuccess: () => {
      toast.success(t('toolbox.zaznamPridan'));
      setNewIp('');
      setNewHostname('');
      qc.invalidateQueries({ queryKey: ['toolbox-hosts'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (hostname: string) => callMcp('toolbox.hosts_entry_remove', { hostname }),
    onSuccess: () => {
      toast.success(t('toolbox.smazano'));
      qc.invalidateQueries({ queryKey: ['toolbox-hosts'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const entries = data?.entries?.filter((e) => !e.comment) ?? [];

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium text-white'>{t('toolbox.souborEtcHosts')}</span>
        <div className='flex gap-2'>
          <RefreshButton onRefresh={() => refetch()} />
          <Button
            variant={editMode ? 'danger' : 'ghost'}
            size='sm'
            onClick={() => {
              if (!editMode && data?.content) setEditContent(data.content);
              setEditMode(!editMode);
            }}
          >
            {editMode ? t('toolbox.zrusit', 'Zrušit') : t('toolbox.rawEdit', 'Raw edit')}
          </Button>
        </div>
      </div>

      {isLoading
        ? (
          <div className='flex justify-center py-4'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : editMode
        ? (
          <div className='space-y-2'>
            <textarea
              className='w-full h-64 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white font-mono resize-y focus:outline-none focus:border-blue-500/50'
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
            />
            <Button
              variant='primary'
              size='sm'
              onClick={() => saveMut.mutate(editContent)}
              loading={saveMut.isPending}
            >
              {t('toolbox.ulozit')}
            </Button>
          </div>
        )
        : (
          <>
            <Card>
              <CardContent className='py-2'>
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='text-gray-500 text-xs'>
                      <th className='text-left py-1 px-2'>IP</th>
                      <th className='text-left py-1 px-2'>{t('toolbox.hostnameS')}</th>
                      <th className='w-8'></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.line} className='border-t border-gray-800/50 hover:bg-gray-900/30'>
                        <td className='py-1.5 px-2 font-mono text-cyan-400'>{e.ip}</td>
                        <td className='py-1.5 px-2 font-mono text-white'>
                          {e.hostnames?.join(', ')}
                        </td>
                        <td className='py-1.5 px-2'>
                          {e.hostnames?.[0] &&
                            ![
                              'localhost',
                              'ip6-localhost',
                              'ip6-loopback',
                              'ip6-allnodes',
                              'ip6-allrouters',
                            ].includes(e.hostnames[0]) && (
                            <button
                              className='text-red-500/50 hover:text-red-400 transition-colors'
                              onClick={() => delMut.mutate(e.hostnames![0])}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardContent className='py-3'>
                <div className='flex gap-2 items-end'>
                  <div className='flex-1'>
                    <span className='text-[11px] text-gray-500'>IP</span>
                    <input
                      className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
                      value={newIp}
                      onChange={(e) => setNewIp(e.target.value)}
                      placeholder='10.119.251.x'
                    />
                  </div>
                  <div className='flex-1'>
                    <span className='text-[11px] text-gray-500'>Hostname</span>
                    <input
                      className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
                      value={newHostname}
                      onChange={(e) => setNewHostname(e.target.value)}
                      placeholder='server.local'
                    />
                  </div>
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={() => {
                      if (newIp && newHostname) {
                        addMut.mutate({
                          ip: newIp,
                          hostname: newHostname,
                        });
                      }
                    }}
                    loading={addMut.isPending}
                  >
                    {t('toolbox.pridat')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
    </div>
  );
}

// ─── Disk Tab (GParted-style) ──────────────────────────────────────────────

function DiskTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [resizeState, setResizeState] = useState<
    {
      device: string;
      partName: string;
      partNum: number;
      fstype: string;
      sizeHuman: string;
      sizeBytes: number;
    } | null
  >(null);
  const [dryRun, setDryRun] = useState(true);
  const [confirm, setConfirm] = useState(false);
  /** Odpověď `toolbox.disk_resize` — viz modules/toolbox/tools/disk_resize.ts. */
  type DiskResizeResult = {
    ok: boolean;
    error?: string;
    message?: string;
    before?: { size_bytes: number; size_human: string };
    after?: { size_bytes: number; size_human: string };
    fstype?: string;
    steps?: Array<{ step: string; message: string; ok?: boolean }>;
    plan?: string[];
  };
  const [resizeResult, setResizeResult] = useState<DiskResizeResult | null>(null);
  const [loading, setLoading] = useState(false);

  const { data: diskData, isLoading, refetch } = useQuery({
    queryKey: ['disk-info'],
    queryFn: () => callMcp('toolbox.disk_info', { detailed: true }),
    staleTime: 30_000,
  });

  const disks = (diskData as {
    disks?: Array<
      {
        device: string;
        model?: string;
        size_bytes: number;
        size_human: string;
        partitions: Array<
          {
            name: string;
            device: string;
            size_bytes: number;
            size_human: string;
            fstype: string;
            mountpoint: string;
          }
        >;
      }
    >;
  })?.disks ?? [];
  // Volné místo za kořenem: rozšíří se při startu uzlu (acm-grow-root.service).
  // Panel nabídne restart, ne zásah do disku (Filip 2026-11-03).
  const rootGrowth = (diskData as {
    root_growth?: {
      partition: string;
      free_after_bytes: number;
      blocked_by: string | null;
      grows_on_restart: boolean;
    } | null;
  })?.root_growth ?? null;
  const restartToGrow = async () => {
    if (!rootGrowth) return;
    const gb = (rootGrowth.free_after_bytes / 1024 ** 3).toFixed(1);
    const ok = await themedConfirm({
      title: t('toolbox.rootGrowthConfirmTitle', 'Restartovat uzel a rozšířit disk?'),
      description: t(
        'toolbox.rootGrowthConfirmText',
        'Uzel se restartuje. Při startu se {{partition}} roztáhne o {{gb}} GB. Služby budou chvíli nedostupné.',
        { partition: rootGrowth.partition, gb },
      ),
    });
    if (!ok) return;
    try {
      const res = await callMcp<{ ok?: boolean; error?: string; message?: string }>(
        'toolbox.sysupdate_reboot',
        { when: 'now', confirm: true },
      );
      if (res?.ok === false) toast.error(res.message || res.error || 'reboot');
      else toast.success(t('toolbox.rootGrowthRebooting', 'Uzel se restartuje, disk se rozšíří při startu.'));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const usage = (diskData as {
    usage?: Array<
      {
        filesystem: string;
        size_bytes: number;
        used_bytes: number;
        free_bytes: number;
        use_pct: number;
        mountpoint: string;
      }
    >;
  })?.usage ?? [];

  const openResize = (
    disk: { device: string; model?: string; size_human: string; size_bytes: number },
    part: { name: string; device: string; size_bytes: number; size_human: string; fstype: string },
  ) => {
    const partNum = parseInt(part.name.replace(/\D/g, ''), 10);
    setResizeState({
      device: disk.device,
      partName: part.name,
      partNum,
      fstype: part.fstype,
      sizeHuman: part.size_human,
      sizeBytes: part.size_bytes,
    });
    setResizeResult(null);
    setDryRun(true);
    setConfirm(false);
  };

  const runResize = async () => {
    if (!resizeState) return;
    setLoading(true);
    try {
      const res = await callMcp<DiskResizeResult>('toolbox.disk_resize', {
        device: resizeState.device,
        partition: resizeState.partNum,
        dry_run: dryRun,
        confirm: confirm || dryRun,
      });
      setResizeResult(res);
      if (!res.ok) {
        // Zásah do disku jen s master key uzlu (Filip 2026-11-03) — z UI se odmítne.
        toast.error(res.message || res.error || t('toolbox.resizeSelhal', 'Změna velikosti selhala'));
      } else if (!dryRun) {
        toast.success(t('toolbox.resizeDokoncen'));
        qc.invalidateQueries({ queryKey: ['disk-info'] });
      } else if (dryRun) {
        toast.info(t('toolbox.simulaceDokoncena'));
      }
    } catch (e) {
      toast.error((e as Error).message);
    }
    setLoading(false);
  };

  const closeResize = () => {
    setResizeState(null);
    setResizeResult(null);
  };

  const getFstypeColor = (fstype: string) => {
    if (fstype === 'ext4' || fstype === 'ext3' || fstype === 'ext2') return 'bg-blue-500';
    if (fstype === 'xfs') return 'bg-green-500';
    if (fstype === 'swap') return 'bg-yellow-500';
    if (fstype === 'btrfs') return 'bg-cyan-500';
    if (fstype === 'ntfs') return 'bg-purple-500';
    return 'bg-gray-500';
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='text-sm text-gray-500'>
          {disks.length} {t('toolbox.disku', 'disků,')}{' '}
          {disks.reduce((s: number, d: { partitions: unknown[] }) => s + d.partitions.length, 0)}
          {' '}
          {t('toolbox.oddilu', 'oddílů')}
        </div>
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} size='sm' />
      </div>

      {rootGrowth?.grows_on_restart && (
        <Alert variant='info'>
          <AlertDescription>
            {t(
              'toolbox.rootGrowthAvailable',
              'Za {{partition}} je {{gb}} GB volného místa. Rozšíří se při příštím startu uzlu.',
              {
                partition: rootGrowth.partition,
                gb: (rootGrowth.free_after_bytes / 1024 ** 3).toFixed(1),
              },
            )}
          </AlertDescription>
          <Button size='sm' variant='primary' className='mt-2' onClick={restartToGrow}>
            {t('toolbox.rootGrowthRestart', 'Restartovat a rozšířit')}
          </Button>
        </Alert>
      )}
      {rootGrowth?.blocked_by && (
        <Alert variant='warning'>
          <AlertDescription>
            {t(
            'toolbox.rootGrowthBlocked',
            'Za {{partition}} leží oddíl {{blocked}}, při startu se nerozšíří. Starý swap oddíl převede servisní skript acm-grow-root-swap-to-file.sh (spouští správce přes SSH).',
              { partition: rootGrowth.partition, blocked: rootGrowth.blocked_by },
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Disk overview */}
      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : disks.length === 0
        ? (
          <EmptyState
            icon={<HardDrive size={24} />}
            title={t('toolbox.zadneDisky')}
            description={t('toolbox.nelzeNacistInformaceO')}
          />
        )
        : (
          <div className='space-y-4'>
            {disks.map((disk) => (
              <Card key={disk.device}>
                <CardHeader>
                  <div className='flex items-center justify-between'>
                    <CardTitle icon={HardDrive}>
                      {disk.device} — {disk.model || 'Unknown'}
                    </CardTitle>
                    <Badge variant='info'>{disk.size_human}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* GParted-style disk visualization */}
                  <div className='mb-4'>
                    <div className='flex items-center gap-2 mb-2'>
                      <div className='flex-1 h-8 rounded overflow-hidden flex border border-gray-700'>
                        {disk.partitions.map((part) => {
                          const pct = disk.size_bytes > 0
                            ? (part.size_bytes / disk.size_bytes) * 100
                            : 0;
                          return (
                            <div
                              key={part.device}
                              className={`${
                                getFstypeColor(part.fstype)
                              } flex items-center justify-center text-[10px] font-bold text-white cursor-pointer hover:opacity-80 transition-opacity relative group`}
                              style={{ width: `${pct}%`, minWidth: pct > 5 ? 'auto' : '2px' }}
                              onClick={() => openResize(disk, part)}
                              title={`${part.name} (${part.fstype || 'none'}) ${part.size_human}${
                                part.mountpoint ? ` → ${part.mountpoint}` : ''
                              }`}
                            >
                              {pct > 15 && <span className='truncate px-1'>{part.name}</span>}
                              {/* Tooltip */}
                              <div className='absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 bg-gray-900 border border-gray-700 rounded-lg p-2 shadow-xl whitespace-nowrap'>
                                <p className='text-xs font-bold text-white'>{part.name}</p>
                                <p className='text-[10px] text-gray-400'>{part.fstype || 'none'}</p>
                                <p className='text-[10px] text-gray-400'>{part.size_human}</p>
                                {part.mountpoint && (
                                  <p className='text-[10px] text-blue-400'>{part.mountpoint}</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {/* Unallocated space */}
                        {(() => {
                          const used = disk.partitions.reduce((s, p) => s + p.size_bytes, 0);
                          const freePct = disk.size_bytes > 0
                            ? ((disk.size_bytes - used) / disk.size_bytes) * 100
                            : 0;
                          if (freePct <= 0) return null;
                          return (
                            <div
                              className='bg-gray-800 flex items-center justify-center text-[10px] text-gray-600'
                              style={{
                                width: `${freePct}%`,
                                minWidth: freePct > 5 ? 'auto' : '2px',
                              }}
                            >
                              {freePct > 15 && <span>free</span>}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    {/* Legend */}
                    <div className='flex flex-wrap gap-3 text-[10px] text-gray-500'>
                      {Array.from(new Set(disk.partitions.map((p) => p.fstype).filter(Boolean)))
                        .map((fstype) => (
                          <div key={fstype} className='flex items-center gap-1'>
                            <div className={`w-2 h-2 rounded-sm ${getFstypeColor(fstype)}`} />
                            <span>{fstype}</span>
                          </div>
                        ))}
                      <div className='flex items-center gap-1'>
                        <div className='w-2 h-2 rounded-sm bg-gray-800 border border-gray-700' />
                        <span>free</span>
                      </div>
                    </div>
                  </div>

                  {/* Partition details */}
                  <div className='space-y-1'>
                    {disk.partitions.map((part) => (
                      <div
                        key={part.device}
                        className='flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-800/50 transition-colors'
                      >
                        <div className={`w-3 h-3 rounded-sm ${getFstypeColor(part.fstype)}`} />
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2'>
                            <span className='font-mono text-sm text-white'>{part.name}</span>
                            <Badge
                              variant={part.fstype === 'ext4'
                                ? 'success'
                                : part.fstype === 'swap'
                                ? 'warning'
                                : 'secondary'}
                            >
                              {part.fstype || 'none'}
                            </Badge>
                          </div>
                          <div className='flex items-center gap-3 mt-0.5'>
                            <span className='text-xs text-gray-400'>{part.size_human}</span>
                            {part.mountpoint && (
                              <>
                                <span className='text-gray-600'>•</span>
                                <span className='text-xs font-mono text-gray-500'>
                                  {part.mountpoint}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => openResize(disk, part)}
                          icon={<ArrowUpCircle size={14} />}
                          className='text-blue-400 hover:text-blue-300'
                        >
                          Resize
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Usage table */}
            {usage.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle icon={Activity}>{t('toolbox.vyuzitiFilesystemu')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-2'>
                    {usage.map((u) => (
                      <div key={u.filesystem} className='flex items-center gap-3 py-1.5'>
                        <span className='font-mono text-xs text-white w-32'>{u.filesystem}</span>
                        <div className='flex-1 h-2 bg-gray-800 rounded-full overflow-hidden'>
                          <div
                            className={`h-full rounded-full ${
                              u.use_pct > 90
                                ? 'bg-red-500'
                                : u.use_pct > 70
                                ? 'bg-amber-500'
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${u.use_pct}%` }}
                          />
                        </div>
                        <span className='text-xs text-gray-400 w-16 text-right'>{u.use_pct}%</span>
                        <span className='text-xs text-gray-500 w-24'>
                          {formatBytes(u.used_bytes)} / {formatBytes(u.size_bytes)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

      {/* Resize modal */}
      {resizeState && (
        <Modal
          title={`Resize ${resizeState.device}${
            resizeState.partName.replace(/\d/g, '')
          }${resizeState.partNum}`}
          open={true}
          onClose={closeResize}
          size='lg'
          footer={
            <div className='flex gap-2'>
              <Button
                variant='primary'
                size='sm'
                onClick={runResize}
                loading={loading}
              >
                {dryRun ? t('toolbox.simulate', 'Simulovat') : t('toolbox.doResize', 'Provést resize')}
              </Button>
              <Button variant='ghost' size='sm' onClick={closeResize}>
                {t('toolbox.zrusit')}
              </Button>
            </div>
          }
        >
          <div className='space-y-4'>
            {/* Description */}
            <p className='text-sm text-gray-400'>
              {dryRun
                ? t('toolbox.resizeDryHint', 'Simulace — nic se nemění. Klikni na "Simulovat" pro zobrazení plánované akce.')
                : t('toolbox.resizeLiveHint', 'Provede se skutečná změna velikosti oddílu a filesystemu. Ujisti se že máš zálohu!')}
            </p>

            {/* Info */}
            <div className='grid grid-cols-2 gap-3'>
              <div className='bg-gray-900 border border-gray-800 rounded-lg p-3'>
                <p className='text-xs font-semibold text-gray-500 mb-1'>{t('toolbox.zarizeni')}</p>
                <p className='text-sm font-mono text-white'>{resizeState.device}</p>
              </div>
              <div className='bg-gray-900 border border-gray-800 rounded-lg p-3'>
                <p className='text-xs font-semibold text-gray-500 mb-1'>{t('toolbox.oddil')}</p>
                <p className='text-sm font-mono text-white'>{resizeState.partName}</p>
              </div>
              <div className='bg-gray-900 border border-gray-800 rounded-lg p-3'>
                <p className='text-xs font-semibold text-gray-500 mb-1'>{t('toolbox.typFs')}</p>
                <Badge
                  variant={resizeState.fstype === 'ext4'
                    ? 'success'
                    : resizeState.fstype === 'swap'
                    ? 'warning'
                    : 'secondary'}
                >
                  {resizeState.fstype}
                </Badge>
              </div>
              <div className='bg-gray-900 border border-gray-800 rounded-lg p-3'>
                <p className='text-xs font-semibold text-gray-500 mb-1'>{t('toolbox.aktualniVelikost')}</p>
                <p className='text-sm font-bold text-white'>{resizeState.sizeHuman}</p>
              </div>
            </div>

            {/* GParted-style visualization */}
            <div>
              <p className='text-xs font-semibold text-gray-400 mb-2'>{t('toolbox.rozlozeniDisku')}</p>
              <div className='flex h-10 rounded overflow-hidden border border-gray-700'>
                {disks.find((d) => d.device === resizeState.device)?.partitions.map((part) => {
                  const disk = disks.find((d) => d.device === resizeState.device);
                  const total = disk?.size_bytes ?? 0;
                  const pct = total > 0 ? (part.size_bytes / total) * 100 : 0;
                  const isResized = part.name === resizeState.partName;
                  return (
                    <div
                      key={part.device}
                      className={`${
                        getFstypeColor(part.fstype)
                      } flex items-center justify-center text-[10px] font-bold text-white relative ${
                        isResized ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''
                      }`}
                      style={{ width: `${pct}%`, minWidth: pct > 5 ? 'auto' : '2px' }}
                    >
                      {pct > 15 && <span className='truncate px-1'>{part.name}</span>}
                    </div>
                  );
                })}
                {(() => {
                  const disk = disks.find((d) => d.device === resizeState.device);
                  const used = disk?.partitions.reduce((s, p) => s + p.size_bytes, 0) ?? 0;
                  const total = disk?.size_bytes ?? 0;
                  const freePct = total > 0 ? ((total - used) / total) * 100 : 0;
                  if (freePct <= 0) return null;
                  return (
                    <div
                      className='bg-gray-800 flex items-center justify-center text-[10px] text-gray-600'
                      style={{ width: `${freePct}%`, minWidth: freePct > 5 ? 'auto' : '2px' }}
                    >
                      {freePct > 15 && <span>free</span>}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Options */}
            <div className='flex gap-4'>
              <label className='flex items-center gap-2 text-sm'>
                <input
                  type='checkbox'
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  className='rounded border-gray-700 bg-gray-900'
                />
                {t('toolbox.dryRunSimulace')}
              </label>
              {!dryRun && (
                <label className='flex items-center gap-2 text-sm'>
                  <input
                    type='checkbox'
                    checked={confirm}
                    onChange={(e) => setConfirm(e.target.checked)}
                    className='rounded border-gray-700 bg-gray-900'
                  />
                  <span className='text-red-400 font-medium'>Potvrzuji</span>
                </label>
              )}
            </div>

            {/* Plan */}
            {resizeResult && (resizeResult as { plan?: string[] })?.plan && (
              <div className='bg-gray-900 border border-gray-800 rounded-lg p-3'>
                <p className='text-xs font-semibold text-gray-400 mb-2'>{t('toolbox.planAkce')}</p>
                {((resizeResult as { plan?: string[] }).plan ?? []).map((
                  cmd: string,
                  i: number,
                ) => <pre key={i} className='text-xs font-mono text-green-400 py-0.5'>{cmd}</pre>)}
              </div>
            )}

            {/* Before/After */}
            {resizeResult &&
              resizeResult
                ?.before &&
              (
                <div className='grid grid-cols-2 gap-4'>
                  <div className='bg-gray-900 border border-gray-800 rounded-lg p-3'>
                    <p className='text-xs font-semibold text-gray-500 mb-1'>{t('toolbox.pred')}</p>
                    <p className='text-lg font-bold text-white'>
                      {resizeResult.before?.size_human}
                    </p>
                  </div>
                  <div className='bg-gray-900 border border-gray-800 rounded-lg p-3'>
                    <p className='text-xs font-semibold text-blue-400 mb-1'>Po</p>
                    <p className='text-lg font-bold text-white'>
                      {resizeResult.after?.size_human}
                    </p>
                  </div>
                </div>
              )}

            {/* Steps */}
            {resizeResult && resizeResult.steps && (
              <div className='space-y-1'>
                {(resizeResult.steps ??
                  []).map((s: { step: string; message: string }, i: number) => (
                    <div key={i} className='flex items-center gap-2 text-xs'>
                      <StatusDot status={(s as { ok?: boolean }).ok ? 'running' : 'error'} />
                      <span className='font-mono text-gray-400 w-24'>{s.step}</span>
                      <span className='text-gray-500'>{s.message}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Node location (MCP: geo.node_location_*) ───────────────────────────────

function NodeLocationPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['geo-node-location', scopeKeySafe()],
    queryFn: () =>
      callMcp<{
        ok: boolean;
        node_id: string;
        has_position: boolean;
        missing_precise_position: boolean;
        resolution: string;
        public_ip: string | null;
        timezone: string;
        timezone_approx?: { lat: number; lon: number; place: string };
        position: {
          lat: number | null;
          lon: number | null;
          source: string;
          altitude?: number | null;
          updated_at?: string;
        } | null;
        site: {
          rack: string;
          server: string;
          room: string;
          datacenter: string;
          contacts: string;
          notes: string;
          proxmox_node: string;
          proxmox_host: string;
        };
      }>('geo.node_location_get', {}),
    staleTime: 15_000,
  });

  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [timezone, setTimezone] = useState('Europe/Prague');
  const [site, setSite] = useState({
    rack: '',
    server: '',
    room: '',
    datacenter: '',
    contacts: '',
    notes: '',
    proxmox_node: '',
    proxmox_host: '',
  });

  useEffect(() => {
    if (!data?.ok) return;
    if (data.position?.lat != null) setLat(String(data.position.lat));
    if (data.position?.lon != null) setLon(String(data.position.lon));
    if (data.timezone) setTimezone(data.timezone);
    if (data.site) {
      setSite({
        rack: data.site.rack ?? '',
        server: data.site.server ?? '',
        room: data.site.room ?? '',
        datacenter: data.site.datacenter ?? '',
        contacts: data.site.contacts ?? '',
        notes: data.site.notes ?? '',
        proxmox_node: data.site.proxmox_node ?? '',
        proxmox_host: data.site.proxmox_host ?? '',
      });
    }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: (mode: 'coords' | 'site' | 'both') => {
      const args: Record<string, unknown> = {
        timezone,
        ...site,
      };
      if (mode === 'coords' || mode === 'both') {
        const la = Number(lat);
        const lo = Number(lon);
        if (!Number.isFinite(la) || !Number.isFinite(lo)) {
          return Promise.reject(new Error(t('toolbox.locationInvalidCoords', 'Neplatné lat/lon')));
        }
        args.lat = la;
        args.lon = lo;
      }
      return callMcp('geo.node_location_set', args);
    },
    onSuccess: () => {
      toast.success(t('toolbox.locationSaved', 'Poloha / site uloženo'));
      void qc.invalidateQueries({ queryKey: ['geo-node-location'] });
      void qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const detectMut = useMutation({
    mutationFn: () => callMcp('geo.node_location_detect', { force: true }),
    onSuccess: () => {
      toast.success(t('toolbox.locationDetected', 'Poloha detekována (IP / timezone)'));
      void refetch();
      void qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const clearMut = useMutation({
    mutationFn: () => callMcp('geo.node_location_set', { clear_manual: true }),
    onSuccess: () => {
      toast.success(t('toolbox.locationCleared', 'Manuální poloha smazána'));
      void detectMut.mutateAsync();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className='flex justify-center py-8'>
        <Loader2 className='animate-spin text-gray-600' />
      </div>
    );
  }

  const res = data?.resolution ?? 'none';
  const missing = data?.missing_precise_position !== false;

  return (
    <div className='space-y-4'>
      <Card>
        <CardContent className='py-4 space-y-3'>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <CardTitle icon={Server}>
              {t('toolbox.locationTitle', 'Poloha nodu')} — {data?.node_id ?? '—'}
            </CardTitle>
            <div className='flex gap-2'>
              <RefreshButton loading={isFetching} onRefresh={() => void refetch()} />
              <Button
                variant='secondary'
                size='sm'
                loading={detectMut.isPending}
                onClick={() => detectMut.mutate()}
              >
                {t('toolbox.locationDetect', 'Detekovat (IP / TZ)')}
              </Button>
            </div>
          </div>
          <p className='text-xs text-gray-500'>
            {t(
              'toolbox.locationHint',
              'MCP: geo.node_location_get/set/detect. Manuální souřadnice mají prioritu; jinak public IP geo, pak timezone.',
            )}
          </p>
          <div className='flex flex-wrap gap-2 text-xs'>
            <Badge variant={missing ? 'warning' : 'success'}>
              {missing
                ? t('toolbox.locationApprox', 'bez přesné GPS')
                : t('toolbox.locationPrecise', 'přesná poloha')}
            </Badge>
            <Badge variant='default'>source: {res}</Badge>
            {data?.public_ip && (
              <Badge variant='info'>egress {data.public_ip}</Badge>
            )}
            <Badge variant='default'>{data?.timezone ?? '—'}</Badge>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
            <div>
              <Label>Lat</Label>
              <Input
                size='sm'
                className='font-mono'
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                placeholder='50.0755'
              />
            </div>
            <div>
              <Label>Lon</Label>
              <Input
                size='sm'
                className='font-mono'
                value={lon}
                onChange={(e) => setLon(e.target.value)}
                placeholder='14.4378'
              />
            </div>
            <div>
              <Label>Timezone (IANA)</Label>
              <Input
                size='sm'
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder='Europe/Prague'
              />
            </div>
          </div>
          {data?.timezone_approx && (
            <p className='text-[11px] text-gray-600'>
              TZ approx: {data.timezone_approx.place} ({Number(data.timezone_approx.lat).toFixed(3)},{' '}
              {Number(data.timezone_approx.lon).toFixed(3)})
            </p>
          )}
          <div className='flex flex-wrap gap-2'>
            <Button
              variant='primary'
              size='sm'
              loading={saveMut.isPending}
              onClick={() => saveMut.mutate('both')}
            >
              {t('toolbox.locationSaveCoords', 'Uložit polohu')}
            </Button>
            <Button
              variant='secondary'
              size='sm'
              loading={clearMut.isPending}
              onClick={async () => {
                if (await themedConfirm(t('toolbox.locationClearConfirm', 'Smazat manuální polohu a detekovat znovu?'))) {
                  clearMut.mutate();
                }
              }}
            >
              {t('toolbox.locationClear', 'Smazat manual → detect')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className='py-4 space-y-3'>
          <CardTitle icon={HardDrive}>
            {t('toolbox.locationSiteTitle', 'Site / infrastruktura')}
          </CardTitle>
          <p className='text-xs text-gray-500'>
            {t(
              'toolbox.locationSiteHint',
              'Rack, server, kontakty — připraveno pro ingest z Proxmox (proxmox_node / proxmox_host).',
            )}
          </p>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            {(
              [
                ['datacenter', t('toolbox.siteDatacenter', 'Datacenter / lokalita')],
                ['room', t('toolbox.siteRoom', 'Místnost')],
                ['rack', t('toolbox.siteRack', 'Rack')],
                ['server', t('toolbox.siteServer', 'Server / U')],
                ['proxmox_node', 'Proxmox node'],
                ['proxmox_host', 'Proxmox host'],
              ] as const
            ).map(([key, label]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Input
                  size='sm'
                  value={site[key]}
                  onChange={(e) => setSite((s) => ({ ...s, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div>
            <Label>{t('toolbox.siteContacts', 'Technické kontakty')}</Label>
            <Input
              size='sm'
              value={site.contacts}
              onChange={(e) => setSite((s) => ({ ...s, contacts: e.target.value }))}
              placeholder='ops@…, +420…'
            />
          </div>
          <div>
            <Label>{t('toolbox.siteNotes', 'Poznámky')}</Label>
            <Input
              size='sm'
              value={site.notes}
              onChange={(e) => setSite((s) => ({ ...s, notes: e.target.value }))}
            />
          </div>
          <Button
            variant='primary'
            size='sm'
            loading={saveMut.isPending}
            onClick={() => saveMut.mutate('site')}
          >
            {t('toolbox.locationSaveSite', 'Uložit site metadata')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function scopeKeySafe(): string {
  return getToolboxWidgetScope().node ?? 'local';
}

// ─── System Tab (Hostname + Network + DNS + NTP + Location) ─────────────────

function SystemTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [subTab, setSubTab] = useState<
    'info' | 'location' | 'network' | 'dns' | 'ntp' | 'hosts' | 'disk'
  >('info');

  // System info
  const { data: infoData, isLoading: infoLoading } = useQuery({
    queryKey: ['sys-info'],
    queryFn: () => callMcp('system-settings.system_info'),
    staleTime: 30_000,
  });
  const info = infoData as Record<string, unknown> | undefined;

  // Hostname
  const { data: hostData } = useQuery({
    queryKey: ['sys-hostname'],
    queryFn: () => callMcp('system-settings.system_hostname', { action: 'get' }),
    staleTime: 60_000,
  });
  const [editHost, setEditHost] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const host = hostData as { hostname?: string; domain?: string; fqdn?: string } | undefined;
  // Sync z načtených dat přes useEffect — NIKDY setState během renderu (#310-class bug).
  useEffect(() => {
    if (host?.hostname) {
      setEditHost(host.hostname);
      setEditDomain(host.domain ?? '');
    }
  }, [host?.hostname, host?.domain]);

  const hostMut = useMutation({
    mutationFn: () =>
      callMcp('system-settings.system_hostname', {
        action: 'set',
        hostname: editHost,
        domain: editDomain || undefined,
      }),
    onSuccess: () => {
      toast.success(t('toolbox.hostnameUlozen'));
      qc.invalidateQueries({ queryKey: ['sys-hostname'] });
      qc.invalidateQueries({ queryKey: ['sys-info'] });
    },
  });

  // Network
  type NetIface = {
    name: string;
    type: 'docker' | 'wifi' | 'ethernet';
    mac_address: string;
    ip_address: string | null;
    prefixlen: number | null;
    ipv4: Array<{ address: string; prefixlen: number; scope: string }>;
    gateway: string | null;
    ipv6: Array<{ address: string; prefixlen: number; scope: string }>;
    gateway6: string | null;
    dns: string[];
    state: 'up' | 'down';
  };
  const { data: netData, isLoading: netLoading } = useQuery<{
    ok: boolean;
    networks?: NetIface[];
  }>({
    queryKey: ['sys-networks'],
    queryFn: () => callMcp('system-settings.system_network_list'),
    staleTime: 30_000,
  });
  const networks = netData?.networks ?? [];
  const [editIface, setEditIface] = useState<string | null>(null);
  const [ifaceForm, setIfaceForm] = useState({
    method: 'dhcp',
    ip_address: '',
    cidr_prefix: '24',
    gateway: '',
    dns: '',
    metric: '',
    ipv6_method: 'auto',
    ipv6_address: '',
    ipv6_prefix: '64',
    ipv6_gateway: '',
  });

  const netSetMut = useMutation({
    mutationFn: (args: Record<string, unknown>) =>
      callMcp('system-settings.system_network_set', args),
    onSuccess: () => {
      toast.success(t('toolbox.sitNastavena'));
      setEditIface(null);
      qc.invalidateQueries({ queryKey: ['sys-networks'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // DNS
  const { data: dnsData } = useQuery({
    queryKey: ['sys-dns'],
    queryFn: () => callMcp('system-settings.system_dns', { action: 'get' }),
    staleTime: 60_000,
  });
  const [dnsPrimary, setDnsPrimary] = useState('');
  const [dnsSecondary, setDnsSecondary] = useState('');
  const dns = dnsData as { primary_dns?: string; secondary_dns?: string } | undefined;
  useEffect(() => {
    if (dns?.primary_dns) {
      setDnsPrimary(dns.primary_dns);
      setDnsSecondary(dns.secondary_dns ?? '');
    }
  }, [dns?.primary_dns, dns?.secondary_dns]);
  const dnsMut = useMutation({
    mutationFn: () =>
      callMcp('system-settings.system_dns', {
        action: 'set',
        primary_dns: dnsPrimary,
        secondary_dns: dnsSecondary,
      }),
    onSuccess: () => {
      toast.success(t('toolbox.dnsUlozeno'));
      qc.invalidateQueries({ queryKey: ['sys-dns'] });
    },
  });

  // NTP
  const { data: ntpData } = useQuery({
    queryKey: ['sys-ntp'],
    queryFn: () => callMcp('system-settings.system_ntp', { action: 'get' }),
    staleTime: 60_000,
  });
  const [ntpServers, setNtpServers] = useState('');
  const ntp = ntpData as { servers?: string[] } | undefined;
  useEffect(() => {
    if (ntp?.servers?.length) setNtpServers(ntp.servers.join(', '));
  }, [ntp?.servers]);
  const ntpMut = useMutation({
    mutationFn: () =>
      callMcp('system-settings.system_ntp', {
        action: 'set',
        servers: ntpServers.split(',').map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: () => {
      toast.success(t('toolbox.ntpUlozeno'));
      qc.invalidateQueries({ queryKey: ['sys-ntp'] });
    },
  });

  const fmtB = (b: number) => {
    if (!b) return '0';
    const u = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(b) / Math.log(1024));
    return (b / Math.pow(1024, i)).toFixed(1) + ' ' + u[i];
  };
  const fmtUp = (s: number) => {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    return d > 0 ? d + 'd ' + h + 'h' : h + 'h';
  };
  const cpu = (info?.cpu ?? {}) as Record<string, unknown>;
  const mem = (info?.memory ?? {}) as Record<string, number>;

  return (
    <div className='space-y-4'>
      {/* Node-switchable monitoring proužek nad management (hostname/DNS/NTP) */}
      <ModulePanel panel={TOOLBOX_SYSTEM} />
      {/* Podzáložky správy systému — sdílené Tabs z design systému. Dřív holé
          šedé tlačítko, Filip 2026-11-03: „nevýrazná celá sekce". */}
      <Tabs defaultTab={subTab} onTabChange={(id) => setSubTab(id as typeof subTab)}>
        <TabList>
          <Tab id='info' icon={<Server size={16} />}>{t('toolbox.tabSystem', 'Systém')}</Tab>
          <Tab id='location' icon={<MapPin size={16} />}>{t('toolbox.tabLocation', 'Poloha')}</Tab>
          <Tab id='network' icon={<Network size={16} />}>{t('toolbox.tabNetwork', 'Síť')}</Tab>
          <Tab id='dns' icon={<Globe size={16} />}>DNS</Tab>
          <Tab id='ntp' icon={<Clock size={16} />}>NTP</Tab>
          <Tab id='hosts' icon={<FileText size={16} />}>Hosts</Tab>
          <Tab id='disk' icon={<HardDrive size={16} />}>Disk</Tab>
        </TabList>
      </Tabs>

      {subTab === 'location' && <NodeLocationPanel />}

      {subTab === 'info' && (
        infoLoading
          ? (
            <div className='flex justify-center py-8'>
              <Loader2 className='animate-spin text-gray-600' />
            </div>
          )
          : (
            <div className='space-y-4'>
              <StatsGrid columns={4}>
                <StatCard label='Hostname' value={String(info?.hostname ?? '—')} icon={Server} />
                <StatCard label='OS' value={String(info?.os ?? '—')} icon={HardDrive} />
                <StatCard
                  label='CPU'
                  value={cpu.cores + ' cores / ' + cpu.load + '%'}
                  icon={Activity}
                />
                <StatCard label='Uptime' value={fmtUp(Number(info?.uptime) || 0)} icon={Clock} />
              </StatsGrid>
              <StatsGrid columns={3}>
                <StatCard
                  label='RAM'
                  value={fmtB(mem.used ?? 0) + ' / ' + fmtB(mem.total ?? 0)}
                  icon={Database}
                />
                <StatCard
                  label='Disk'
                  value={fmtB(Number((info?.disk as Record<string, number>)?.used) || 0) + ' / ' +
                    fmtB(Number((info?.disk as Record<string, number>)?.total) || 0)}
                  icon={HardDrive}
                />
                <StatCard label='Kernel' value={String(info?.kernel ?? '—')} icon={Shield} />
              </StatsGrid>
              {/* Hostname/domain edit */}
              <Card>
                <CardContent className='space-y-3 py-4'>
                  <CardTitle icon={Server}>{t('toolbox.hostnameADomena')}</CardTitle>
                  <div className='grid grid-cols-2 gap-3 mt-3'>
                    <div>
                      <span className='text-[11px] text-gray-500'>Hostname</span>
                      <Input
                        className='mt-0.5'
                        value={editHost}
                        onChange={(e) => setEditHost(e.target.value)}
                      />
                    </div>
                    <div>
                      <span className='text-[11px] text-gray-500'>{t('toolbox.domena')}</span>
                      <Input
                        className='mt-0.5'
                        value={editDomain}
                        onChange={(e) => setEditDomain(e.target.value)}
                        placeholder='example.local'
                      />
                    </div>
                  </div>
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={() => hostMut.mutate()}
                    loading={hostMut.isPending}
                  >
                    {t('toolbox.ulozitHostname')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )
      )}

      {subTab === 'network' && (
        <div className='space-y-2'>
          {netLoading
            ? (
              <div className='flex justify-center py-4'>
                <Loader2 className='animate-spin text-gray-600' />
              </div>
            )
            : networks.length === 0
            ? (
              <EmptyState
                icon={<Network size={24} />}
                title={t('toolbox.zadnaRozhrani')}
                description={t('toolbox.nenalezena')}
              />
            )
            : networks.filter((n) =>
              !['docker', 'veth'].some((p) => String(n.name).startsWith(p)) &&
              !String(n.name).startsWith('br-')
            ).map((n, i) => (
              <Card key={i}>
                <CardContent className='py-3'>
                  <div className='flex items-center gap-3'>
                    <StatusDot status={n.state === 'up' ? 'running' : 'error'} />
                    <span className='font-mono text-sm text-white flex-1'>{n.name}</span>
                    <span className='text-xs text-gray-400 font-mono'>
                      {n.ip_address ?? '—'}
                      {n.prefixlen ? '/' + n.prefixlen : ''}
                    </span>
                    {n.gateway && (
                      <span className='text-xs text-gray-600'>gw: {n.gateway}</span>
                    )}
                    {(n.ipv6 ?? [])
                      .filter((a) => a.scope === 'global').map((a, j) => (
                        <span key={j} className='text-xs text-cyan-400 font-mono'>
                          {a.address}/{a.prefixlen}
                        </span>
                      ))}
                    {n.gateway6 && (
                      <span className='text-xs text-gray-600'>gw6: {n.gateway6}</span>
                    )}
                    <Badge>{n.type}</Badge>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => {
                        setEditIface(String(n.name));
                        setIfaceForm((f) => ({
                          ...f,
                          method: 'static',
                          ip_address: String(n.ip_address ?? ''),
                          cidr_prefix: String(n.prefixlen ?? 24),
                          gateway: String(n.gateway ?? ''),
                          dns: ((n.dns as string[]) ?? []).join(', '),
                        }));
                      }}
                    >
                      Nastavit
                    </Button>
                  </div>
                  {editIface === String(n.name) && (
                    <div className='mt-3 pt-3 border-t border-gray-800 space-y-3'>
                      <div className='flex gap-3 items-end'>
                        <div className='w-24'>
                          <span className='text-[11px] text-gray-500'>Metoda</span>
                          <Select
                            className='mt-0.5 w-full'
                            value={ifaceForm.method}
                            onChange={(e) =>
                              setIfaceForm((f) => ({ ...f, method: e.target.value }))}
                          >
                            <option value='dhcp'>DHCP</option>
                            <option value='static'>Static</option>
                          </Select>
                        </div>
                        {ifaceForm.method === 'static' && (
                          <>
                            <div className='flex-1'>
                              <span className='text-[11px] text-gray-500'>IP</span>
                              <input
                                className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono'
                                value={ifaceForm.ip_address}
                                onChange={(e) =>
                                  setIfaceForm((f) => ({ ...f, ip_address: e.target.value }))}
                              />
                            </div>
                            <div className='w-16'>
                              <span className='text-[11px] text-gray-500'>CIDR</span>
                              <input
                                className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-2 py-1.5 text-sm text-white'
                                value={ifaceForm.cidr_prefix}
                                onChange={(e) =>
                                  setIfaceForm((f) => ({ ...f, cidr_prefix: e.target.value }))}
                              />
                            </div>
                            <div className='flex-1'>
                              <span className='text-[11px] text-gray-500'>Gateway</span>
                              <input
                                className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono'
                                value={ifaceForm.gateway}
                                onChange={(e) =>
                                  setIfaceForm((f) => ({ ...f, gateway: e.target.value }))}
                              />
                            </div>
                            <div className='w-20'>
                              <span className='text-[11px] text-gray-500'>Metrika</span>
                              <input
                                className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-2 py-1.5 text-sm text-white'
                                value={ifaceForm.metric}
                                onChange={(e) =>
                                  setIfaceForm((f) => ({ ...f, metric: e.target.value }))}
                                placeholder='100'
                              />
                            </div>
                          </>
                        )}
                      </div>
                      {/* IPv6 */}
                      <div className='flex gap-3 items-end'>
                        <div className='w-24'>
                          <span className='text-[11px] text-cyan-400'>IPv6</span>
                          <Select
                            className='mt-0.5 w-full'
                            value={ifaceForm.ipv6_method}
                            onChange={(e) =>
                              setIfaceForm((f) => ({ ...f, ipv6_method: e.target.value }))}
                          >
                            <option value='auto'>SLAAC</option>
                            <option value='static'>Static</option>
                            <option value='disabled'>Off</option>
                          </Select>
                        </div>
                        {ifaceForm.ipv6_method === 'static' && (
                          <>
                            <div className='flex-1'>
                              <span className='text-[11px] text-gray-500'>{t('toolbox.ipv6Adresa')}</span>
                              <input
                                className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono'
                                value={ifaceForm.ipv6_address}
                                onChange={(e) =>
                                  setIfaceForm((f) => ({ ...f, ipv6_address: e.target.value }))}
                                placeholder='2001:db8::1'
                              />
                            </div>
                            <div className='w-16'>
                              <span className='text-[11px] text-gray-500'>/</span>
                              <input
                                className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-2 py-1.5 text-sm text-white'
                                value={ifaceForm.ipv6_prefix}
                                onChange={(e) =>
                                  setIfaceForm((f) => ({ ...f, ipv6_prefix: e.target.value }))}
                              />
                            </div>
                            <div className='flex-1'>
                              <span className='text-[11px] text-gray-500'>{t('toolbox.ipv6Gw')}</span>
                              <input
                                className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono'
                                value={ifaceForm.ipv6_gateway}
                                onChange={(e) =>
                                  setIfaceForm((f) => ({ ...f, ipv6_gateway: e.target.value }))}
                              />
                            </div>
                          </>
                        )}
                      </div>
                      <div className='flex gap-2'>
                        <Button
                          variant='primary'
                          size='sm'
                          loading={netSetMut.isPending}
                          onClick={() =>
                            netSetMut.mutate({
                              name: editIface,
                              method: ifaceForm.method,
                              ...(ifaceForm.method === 'static'
                                ? {
                                  ip_address: ifaceForm.ip_address,
                                  cidr_prefix: parseInt(ifaceForm.cidr_prefix),
                                  gateway: ifaceForm.gateway || undefined,
                                  metric: ifaceForm.metric ? parseInt(ifaceForm.metric) : undefined,
                                }
                                : {}),
                              ipv6_method: ifaceForm.ipv6_method,
                              ...(ifaceForm.ipv6_method === 'static'
                                ? {
                                  ipv6_address: ifaceForm.ipv6_address,
                                  ipv6_prefix: parseInt(ifaceForm.ipv6_prefix),
                                  ipv6_gateway: ifaceForm.ipv6_gateway || undefined,
                                }
                                : {}),
                            })}
                        >
                          Aplikovat
                        </Button>
                        <Button variant='ghost' size='sm' onClick={() => setEditIface(null)}>
                          {t('toolbox.zrusit')}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {subTab === 'dns' && (
        <Card>
          <CardContent className='space-y-3 py-4'>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <span className='text-[11px] text-gray-500'>{t('toolbox.primarniDns')}</span>
                <input
                  className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white'
                  value={dnsPrimary}
                  onChange={(e) => setDnsPrimary(e.target.value)}
                  placeholder='1.1.1.1'
                />
              </div>
              <div>
                <span className='text-[11px] text-gray-500'>{t('toolbox.sekundarniDns')}</span>
                <input
                  className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white'
                  value={dnsSecondary}
                  onChange={(e) => setDnsSecondary(e.target.value)}
                  placeholder='8.8.8.8'
                />
              </div>
            </div>
            <Button
              variant='primary'
              size='sm'
              onClick={() => dnsMut.mutate()}
              loading={dnsMut.isPending}
            >
              {t('toolbox.ulozitDns')}
            </Button>
          </CardContent>
        </Card>
      )}

      {subTab === 'ntp' && (
        <Card>
          <CardContent className='space-y-3 py-4'>
            <div>
              <span className='text-[11px] text-gray-500'>{t('toolbox.ntpServery')}</span>
              <input
                className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white'
                value={ntpServers}
                onChange={(e) => setNtpServers(e.target.value)}
                placeholder={t('toolbox.0PoolNtpOrg')}
              />
            </div>
            <Button
              variant='primary'
              size='sm'
              onClick={() => ntpMut.mutate()}
              loading={ntpMut.isPending}
            >
              {t('toolbox.ulozitNtp')}
            </Button>
          </CardContent>
        </Card>
      )}

      {subTab === 'hosts' && <HostsEditor />}
      {subTab === 'disk' && <DiskTab />}
    </div>
  );
}


// ─── Widget panel wrappers (2.1) ────────────────────────────────────────────

export function HealthPanel({ scope }: WidgetRenderProps) {
  setToolboxWidgetScope(scope ?? { node: null, identity: null });
  return <HealthTab />;
}

export function SecurityPanel({ scope }: WidgetRenderProps) {
  setToolboxWidgetScope(scope ?? { node: null, identity: null });
  return <SecurityTab />;
}

export function ToolboxBackupPanel({ scope }: WidgetRenderProps) {
  setToolboxWidgetScope(scope ?? { node: null, identity: null });
  return <BackupTab />;
}

export function CleanerPanel({ scope }: WidgetRenderProps) {
  setToolboxWidgetScope(scope ?? { node: null, identity: null });
  return <CleanerTab />;
}

export function WatchdogPanel({ scope }: WidgetRenderProps) {
  setToolboxWidgetScope(scope ?? { node: null, identity: null });
  return <WatchdogTab />;
}

export function CrashRecoveryPanel({ scope }: WidgetRenderProps) {
  setToolboxWidgetScope(scope ?? { node: null, identity: null });
  return <CrashRecoveryTab />;
}

export function ResourcesPanel({ scope }: WidgetRenderProps) {
  setToolboxWidgetScope(scope ?? { node: null, identity: null });
  return <ResourceTab />;
}

export function SystemPanel({ scope }: WidgetRenderProps) {
  setToolboxWidgetScope(scope ?? { node: null, identity: null });
  return <SystemTab />;
}

export function AcmLinkPanel({ scope }: WidgetRenderProps) {
  setToolboxWidgetScope(scope ?? { node: null, identity: null });
  return <AcmLinkPage />;
}
